import { QBSLevelCheckboxGroup } from '@exam-management/components';
import { QBankStructureLevelCategory, useQBankStructure } from '@exam-management/qbs-provider';
import { Alert, ConfirmationModal, Divider, Flex, Form } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import QBankStructureLevelFormAction from './qbank-structure-level-form-action';

interface QBankStructureLevelFormProps {
  loading?: boolean;
  onClose: () => void;
  onSubmit: (formValues: QBankStructureLevelStatusByCategory) => Promise<void>;
}

export type QBankStructureLevelStatusByCategory = {
  [key in QBankStructureLevelCategory]: boolean;
};

export default function QBankStructureLevelForm(props: Readonly<QBankStructureLevelFormProps>) {
  const [isConfirmationModalOpen, { open: openConfirmationModal, close: closeConfirmationModal }] =
    useDisclosure();
  const { loading: isLoading, onClose, onSubmit } = props;

  const { qBankStructureLevels } = useQBankStructure();

  const formContext = useForm<QBankStructureLevelStatusByCategory>();

  const isDirty = formContext.formState.isDirty;

  const handleSaveSetting = async () => {
    await onSubmit(formContext.getValues());
    closeConfirmationModal();
  };

  useEffect(() => {
    formContext.reset(
      qBankStructureLevels.reduce((acc, { category, enabled }) => {
        if (
          [
            QBankStructureLevelCategory.Course,
            QBankStructureLevelCategory.Chapter,
            QBankStructureLevelCategory.SubTopic,
          ].includes(category)
        ) {
          acc[category] = undefined;
        } else {
          acc[category] = enabled;
        }
        return acc;
      }, {} as Partial<QBankStructureLevelStatusByCategory>),
    );
  }, [qBankStructureLevels]);

  return (
    <>
      <FormProvider {...formContext}>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            openConfirmationModal();
          }}
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              p: theme.spacing(4, 6, 6, 6),
              gap: theme.spacing(4),
              overflow: 'auto',
              maxHeight: '480px',
            })}
          >
            {isDirty ? (
              <Alert
                severity='warning'
                sx={{
                  '&.MuiPaper-root': {
                    width: 'unset',
                  },
                }}
              >
                การปิดระดับโครงสร้างจะทำให้ข้อมูลที่อยู่ภายใต้ระดับนั้นๆ
                ถูกย้ายไปรวมกับระดับที่สูงกว่า
              </Alert>
            ) : null}
            <QBSLevelCheckboxGroup qBankStructureLevels={qBankStructureLevels} />
          </Flex>
          <Divider />
          <Flex
            sx={(theme) => ({
              justifyContent: 'flex-end',
              p: theme.spacing(6),
              gap: theme.spacing(3),
            })}
          >
            <QBankStructureLevelFormAction onClose={onClose} />
          </Flex>
        </Form>
      </FormProvider>
      <ConfirmationModal
        open={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
        onDiscard={closeConfirmationModal}
        onAccept={handleSaveSetting}
        title='Structure Level Change?'
        content='ยืนยันการเปลี่ยนแปลงระดับโครงสร้าง (Structure Level)'
        acceptButtonProps={{
          sx: {
            minWidth: 'auto',
          },
        }}
        loading={isLoading}
      />
    </>
  );
}
